/**
 * Barra de filtros globales del informe (no recortan la fuente).
 */
import React, { useEffect, useRef, useState } from 'react';
import { defaultFilterType, normalizeFilterValues } from './filterUtils';
import { sourceRequestFields, sourceLabel } from './engineConfig';
import FilterDropdown from './FilterDropdown';
import { studio, FilterDock, FilterDate, FilterPill, FilterMeta, ink } from '../studioUi';

const TYPE_OPTIONS = [
    { value: 'multiselect', label: 'Lista' },
    { value: 'dateRange', label: 'Rango de fechas' },
    { value: 'text', label: 'Texto' },
];

function DebouncedText({ value, onCommit, placeholder }) {
    const [draft, setDraft] = useState(value || '');
    const commitRef = useRef(onCommit);
    commitRef.current = onCommit;
    const valueRef = useRef(value);
    valueRef.current = value;
    useEffect(() => { setDraft(value || ''); }, [value]);
    useEffect(() => {
        const handle = setTimeout(() => {
            if (draft !== (valueRef.current || '')) commitRef.current(draft);
        }, 400);
        return () => clearTimeout(handle);
    }, [draft]);
    return (
        <input
            type="text"
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { if (draft !== (value || '')) onCommit(draft); }}
            style={studio.control}
        />
    );
}

const DashboardFilterBar = ({
    filterDefs = [],
    filterValues = {},
    schemaColumns = [],
    schemasBySource = {},
    dataSources = [],
    joins = [],
    onValuesChange,
    editable = false,
    onAddFilter,
    onRemoveFilter,
    dataSource,
    elevated = false,
}) => {
    const [columnToAdd, setColumnToAdd] = useState('');
    const [typeToAdd, setTypeToAdd] = useState('multiselect');
    const [sourceToAdd, setSourceToAdd] = useState(dataSources[0]?.id || '');
    const [composerOpen, setComposerOpen] = useState(false);

    const addSourceId = sourceToAdd || dataSources[0]?.id;
    const addColumns = (addSourceId && schemasBySource[addSourceId]?.columns) || schemaColumns;
    const unusedColumns = addColumns.filter((col) => !filterDefs.some((def) => def.column === col.name && (!def.dataSourceId || def.dataSourceId === addSourceId)));
    const visibleDefs = filterDefs.filter((def) => def.column && !String(def.id).startsWith('slicer-'));

    const updateValue = (id, next) => {
        onValuesChange({ ...filterValues, [id]: next });
    };

    const handleAdd = () => {
        const col = addColumns.find((item) => item.name === columnToAdd);
        if (!col || !onAddFilter) return;
        onAddFilter({
            id: `filter-${Date.now()}`,
            column: col.name,
            label: dataSources.length > 1 ? `${sourceLabel(dataSources.find((s) => s.id === addSourceId))}: ${col.name}` : col.name,
            type: typeToAdd || defaultFilterType(col.inferred_type, col.unique_count),
            options: col.values || [],
            dataSourceId: addSourceId,
        });
        setColumnToAdd('');
        setComposerOpen(false);
    };

    const chips = visibleDefs.flatMap((def) => {
        const val = filterValues[def.id];
        const label = def.label || def.column;
        if (['select', 'dropdown', 'multiselect'].includes(def.type)) {
            const list = normalizeFilterValues(val);
            return list.map((item, index) => ({
                key: `${def.id}-${index}`,
                label: `${label}: ${item}`,
                onClear: () => updateValue(def.id, list.filter((row) => row !== item)),
            }));
        }
        if (def.type === 'dateRange' && val && (val.from || val.to)) {
            return [{
                key: def.id,
                label: `${label}: ${val.from || '…'} → ${val.to || '…'}`,
                onClear: () => updateValue(def.id, {}),
            }];
        }
        if (def.type === 'text' && val) {
            return [{
                key: def.id,
                label: `${label}: ${val}`,
                onClear: () => updateValue(def.id, ''),
            }];
        }
        return [];
    });

    const clearAll = () => {
        const next = { ...filterValues };
        visibleDefs.forEach((def) => { delete next[def.id]; });
        onValuesChange(next);
    };

    if (!editable && visibleDefs.length === 0) return null;

    const openComposer = () => setComposerOpen(true);

    return (
        <FilterDock
            variant={elevated ? 'card' : undefined}
            style={{
                marginTop: 0,
                marginBottom: 0,
                flexShrink: 0,
                ...(composerOpen ? {} : { padding: '10px 14px' }),
            }}
        >
            <div style={styles.head}>
                <div>
                    <strong style={studio.kicker}>Filtros del informe</strong>
                    {visibleDefs.length > 0 && (
                        <span style={styles.count}>
                            {visibleDefs.length} control{visibleDefs.length === 1 ? '' : 'es'}
                        </span>
                    )}
                    {editable && composerOpen && (
                        <p style={styles.legend}>
                            Cortan lo que se ve en los gráficos. El recorte de la hoja está en Fuentes; el de un solo gráfico, en Propiedades.
                        </p>
                    )}
                </div>
                {editable && (
                    composerOpen ? (
                        <button type="button" style={styles.ghost} onClick={() => setComposerOpen(false)}>Listo</button>
                    ) : (
                        <button type="button" style={styles.addTrigger} onClick={openComposer}>+ Filtro</button>
                    )
                )}
            </div>
            {(composerOpen || (!editable && visibleDefs.length > 0)) && (
            <div style={{ ...styles.row, marginTop: 10 }}>
                {visibleDefs.length === 0 && composerOpen && (
                    <span style={studio.filterMetaText}>
                        Elige un campo para crear un control, o coloca un widget Filtro en el lienzo.
                    </span>
                )}
                {visibleDefs.map((def) => {
                    const sourceCols = (def.dataSourceId && schemasBySource[def.dataSourceId]?.columns) || schemaColumns;
                    const colMeta = sourceCols.find((item) => item.name === def.column);
                    const options = colMeta?.values || def.options || [];
                    // Si la lista guardada cubre todos los valores de la columna, el
                    // desplegable no necesita consultar /distinct al abrirse.
                    const optionsComplete = options.length > 0 && (
                        colMeta?.unique_count != null
                            ? colMeta.unique_count <= options.length
                            : def.optionsComplete === true
                    );
                    const value = filterValues[def.id];
                    const filterSource = sourceRequestFields(
                        { dataSourceId: def.dataSourceId, ...(dataSource || {}) },
                        { dataSources, joins },
                    );
                    return (
                        <div key={def.id} style={styles.control}>
                            <div style={styles.controlFields}>
                                {['select', 'dropdown', 'multiselect'].includes(def.type) ? (
                                    <FilterDropdown
                                        label={def.label || def.column}
                                        value={value || []}
                                        options={options}
                                        optionsComplete={optionsComplete}
                                        column={def.column}
                                        dataSource={filterSource}
                                        onChange={(next) => updateValue(def.id, next)}
                                    />
                                ) : def.type === 'dateRange' ? (
                                    <>
                                        <span style={studio.fieldLabel}>{def.label || def.column}</span>
                                        <div style={styles.dateRow}>
                                            <FilterDate value={value?.from || ''} onChange={(from) => updateValue(def.id, { ...(value || {}), from })} title="Desde" />
                                            <FilterDate value={value?.to || ''} onChange={(to) => updateValue(def.id, { ...(value || {}), to })} title="Hasta" />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <span style={studio.fieldLabel}>{def.label || def.column}</span>
                                        <DebouncedText
                                            value={value || ''}
                                            placeholder="Contiene…"
                                            onCommit={(next) => updateValue(def.id, next)}
                                        />
                                    </>
                                )}
                            </div>
                            {editable && (
                                <button type="button" style={styles.remove} onClick={() => onRemoveFilter(def.id)} title="Quitar control">×</button>
                            )}
                        </div>
                    );
                })}

                {editable && composerOpen && unusedColumns.length > 0 && (
                    <div style={styles.addBox}>
                        <span style={studio.fieldLabel}>Nuevo control</span>
                        <div style={studio.filterPills}>
                            {dataSources.length > 1 && (
                                <FilterPill value={addSourceId || ''} onChange={(value) => { setSourceToAdd(value); setColumnToAdd(''); }} emptyLabel="Fuente">
                                    {dataSources.map((source) => (
                                        <option key={source.id} value={source.id}>{sourceLabel(source)}</option>
                                    ))}
                                </FilterPill>
                            )}
                            <FilterPill
                                value={columnToAdd}
                                onChange={(value) => {
                                    setColumnToAdd(value);
                                    const col = addColumns.find((item) => item.name === value);
                                    if (col) setTypeToAdd(defaultFilterType(col.inferred_type, col.unique_count));
                                }}
                                emptyLabel="Campo…"
                            >
                                {unusedColumns.map((col) => (
                                    <option key={col.name} value={col.name}>{col.name}</option>
                                ))}
                            </FilterPill>
                            <FilterPill value={typeToAdd} onChange={setTypeToAdd} emptyLabel="Tipo">
                                {TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FilterPill>
                            <button type="button" style={styles.addBtn} disabled={!columnToAdd} onClick={handleAdd}>Añadir</button>
                        </div>
                    </div>
                )}
            </div>
            )}
            <FilterMeta
                summary={chips.length ? `${chips.length} activo${chips.length === 1 ? '' : 's'}` : ''}
                chips={chips}
                onClearAll={chips.length ? clearAll : undefined}
            />
        </FilterDock>
    );
};

const styles = {
    head: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 0,
    },
    count: {
        marginLeft: 8,
        fontSize: '0.72rem',
        fontWeight: 650,
        color: ink.muted,
    },
    legend: { margin: '6px 0 0', fontSize: '0.75rem', color: ink.muted, lineHeight: 1.4, maxWidth: 640 },
    ghost: {
        appearance: 'none',
        flexShrink: 0,
        padding: '6px 12px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.accentText,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    addTrigger: {
        appearance: 'none',
        flexShrink: 0,
        padding: '6px 12px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 0,
        backgroundColor: ink.accent,
        color: ink.surface,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    row: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'flex-end',
    },
    control: { display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 4 },
    controlFields: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 },
    dateRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    addBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 220,
        padding: '8px 10px 10px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: ink.lineStrong,
        borderRadius: 12,
    },
    remove: {
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        color: ink.danger,
        fontSize: '1rem',
        lineHeight: 1,
        padding: '6px 4px',
        fontFamily: 'inherit',
    },
    addBtn: {
        ...studio.primaryBtn,
        padding: '9px 14px',
        minWidth: 72,
    },
};

export default DashboardFilterBar;
